import React, { useCallback, useMemo, useState } from "react";
import { Form, Grid, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { EventSummary } from "../../../services/drinkService";
import { EventVisibility } from "../../../generated/drinks_pb";
import { buildEventVisibilityOptions } from "../eventVisibilityOptions";
import { createEvent, deleteEvent, endEvent, joinEvent, leaveEvent, updateEventAccess } from "../../../services/drinks/EventService";
import { useTranslation } from "react-i18next";
import { useProfileData } from "./useProfileData";
import { useAppFeedback } from "../../useAppFeedback";
const { useBreakpoint } = Grid;

export const useProfile = () => {
    const { message } = useAppFeedback();
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { t, i18n } = useTranslation();
    const { totalDrinks, lastDrinkAt, loading, profileError, loadProfile, eventsLoading, activeEvent, openEvents, setEventsLoading, loadEvents } = useProfileData(user, isAuthenticated, t);
    const { options: visibilityOptions, optionMap: visibilityOptionMap } = useMemo(() => buildEventVisibilityOptions(t), [t]);
    const [creatingEvent, setCreatingEvent] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [accessForm] = Form.useForm();
    const [accessModalVisible, setAccessModalVisible] = useState<boolean>(false);
    const [updatingAccess, setUpdatingAccess] = useState<boolean>(false);
    const [passwordPrompt, setPasswordPrompt] = useState<{
        event: EventSummary;
        resolve: (value: string | null) => void;
    } | null>(null);
    const [passwordPromptValue, setPasswordPromptValue] = useState<string>("");
    const selectedVisibility = Form.useWatch("visibility", form) as EventVisibility | undefined;
    const selectedAccessVisibility = Form.useWatch("visibility", accessForm) as EventVisibility | undefined;
    const [deletingEvent, setDeletingEvent] = useState<boolean>(false);
    const getVisibilityMeta = useCallback(
        (visibility: EventVisibility) =>
            visibilityOptionMap.get(visibility) ?? visibilityOptions[0],
        [visibilityOptionMap, visibilityOptions]
    );

    const handleCreateEvent = async (values: { eventName: string; location: string; visibility: EventVisibility; password?: string }) => {
        if (!user) {
            return;
        }
        setCreatingEvent(true);
        try {
            const password =
                values.visibility === EventVisibility.EVENT_VISIBILITY_PASSWORD
                    ? values.password ?? ""
                    : null;

            await createEvent(user, values.eventName, values.location, {
                visibility: values.visibility,
                password,
            });
            message.success(t("profile.messages.createSuccess"));
            form.resetFields();
            await loadEvents();
        } catch {
            message.error(t("profile.messages.createError"));
        } finally {
            setCreatingEvent(false);
        }
    };

    const promptForEventPassword = (event: EventSummary): Promise<string | null> =>
        new Promise((resolve) => {
            setPasswordPromptValue("");
            setPasswordPrompt({
                event,
                resolve,
            });
        });

    const closePasswordPrompt = (result: string | null) => {
        if (passwordPrompt) {
            passwordPrompt.resolve(result);
        }
        setPasswordPrompt(null);
        setPasswordPromptValue("");
    };

    const handlePasswordPromptConfirm = () => {
        const trimmed = passwordPromptValue.trim();
        if (!trimmed) {
            message.error(t("profile.messages.passwordPromptEmpty"));
            return;
        }
        closePasswordPrompt(trimmed);
    };

    const handlePasswordPromptCancel = () => {
        closePasswordPrompt(null);
    };

    const attemptJoinEvent = async (
        currentUser: NonNullable<typeof user>,
        event: EventSummary,
        password: string | undefined,
        isPasswordVisibility: boolean
    ): Promise<string | null> => {
        setEventsLoading(true);
        try {
            await joinEvent(currentUser, event, password);
            message.success(t("profile.messages.joinSuccess"));
            await loadEvents();
            return null;
        } catch (error: any) {
            const serverMessage = typeof error?.message === "string" ? error.message : "";
            const passwordIssue = /password required/i.test(serverMessage) ||
                /invalid password/i.test(serverMessage) ||
                (isPasswordVisibility && error?.code === 7);
            if (!passwordIssue) {
                message.error(t("profile.messages.joinError"));
                return null;
            }
            message.error(t("profile.messages.passwordPromptInvalid"));
            return await promptForEventPassword(event);
        } finally {
            setEventsLoading(false);
        }
    };

    const handleJoinEvent = async (event: EventSummary) => {
        if (!user) return;
        const isPasswordVisibility = event.visibility === EventVisibility.EVENT_VISIBILITY_PASSWORD;
        const needsInitialPrompt = event.ownerUserId !== user.userId &&
            !event.isParticipant && (event.requiresPassword || isPasswordVisibility);
        let password: string | undefined;
        if (needsInitialPrompt) {
            password = await promptForEventPassword(event) ?? undefined;
            if (password === undefined) return;
        }

        while (true) {
            const retryPassword = await attemptJoinEvent(user, event, password, isPasswordVisibility);
            if (retryPassword === null) return;
            password = retryPassword;
        }
    };

    const handleLeaveEvent = async () => {
        if (!user || !activeEvent) {
            return;
        }
        setEventsLoading(true);
        try {
            await leaveEvent(user, activeEvent.eventId);
            message.success(t("profile.messages.leaveSuccess"));
            await loadEvents();
        } catch {
            message.error(t("profile.messages.leaveError"));
        } finally {
            setEventsLoading(false);
        }
    };

    const handleEndEvent = async () => {
        if (!user || !activeEvent) {
            return;
        }
        setEventsLoading(true);
        try {
            await endEvent(user, activeEvent.eventId);
            message.success(t("profile.messages.endSuccess"));
            await loadEvents();
        } catch {
            message.error(t("profile.messages.endError"));
        } finally {
            setEventsLoading(false);
        }
    };

    const handleDeleteEvent = async () => {
        if (!user || !activeEvent) {
            return;
        }
        setDeletingEvent(true);
        try {
            await deleteEvent(user, activeEvent.eventId);
            message.success(t("profile.messages.deleteSuccess"));
            await loadEvents();
        } catch {
            message.error(t("profile.messages.deleteError"));
        } finally {
            setDeletingEvent(false);
        }
    };

    const openAccessModal = () => {
        if (!activeEvent) {
            return;
        }
        accessForm.setFieldsValue({
            visibility: activeEvent.visibility,
            password: "",
        });
        setAccessModalVisible(true);
    };

    const closeAccessModal = () => {
        setAccessModalVisible(false);
        accessForm.resetFields();
    };

    const handleUpdateEventAccess = async () => {
        if (!user || !activeEvent) {
            return;
        }

        try {
            const values: { visibility: EventVisibility; password?: string } = await accessForm.validateFields();
            setUpdatingAccess(true);
            const password =
                values.visibility === EventVisibility.EVENT_VISIBILITY_PASSWORD
                    ? values.password ?? ""
                    : null;

            await updateEventAccess(user, activeEvent.eventId, {
                visibility: values.visibility,
                password,
            });
            message.success(t("profile.messages.updateAccessSuccess"));
            closeAccessModal();
            await loadEvents();
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }
            message.error(t("profile.messages.updateAccessError"));
        } finally {
            setUpdatingAccess(false);
        }
    };

    const renderVisibilityTag = (event: EventSummary) => {
        const meta = getVisibilityMeta(event.visibility);
        const suffix =
            event.visibility === EventVisibility.EVENT_VISIBILITY_PASSWORD
                ? t("eventStats.visibilityOptions.passwordSuffix")
                : "";
        return (
            <Tag color={meta.color} key={`${event.eventId}-visibility`}>
                {meta.label ?? t("eventStats.visibilityOptions.unknown")}
                {suffix}
            </Tag>
        );
    };

    return {
        isAuthenticated, user, navigate, screens, isMobile, t, i18n, visibilityOptions, totalDrinks,
        lastDrinkAt, loading, profileError, loadProfile, eventsLoading, creatingEvent, activeEvent, openEvents, form, accessForm,
        accessModalVisible, updatingAccess, passwordPrompt, passwordPromptValue, setPasswordPromptValue,
        selectedVisibility, selectedAccessVisibility, deletingEvent, getVisibilityMeta, loadEvents,
        handleCreateEvent, handleJoinEvent, handleLeaveEvent, handleEndEvent, handleDeleteEvent,
        openAccessModal, closeAccessModal, handleUpdateEventAccess, renderVisibilityTag,
        handlePasswordPromptConfirm, handlePasswordPromptCancel,
    };
};
